// src/pages/FacturacionPresupuestos/ModalEnvioSendGrid.js
import React, { useState, useEffect, useMemo } from 'react';
import { 
    FaEnvelope, 
    FaTimes, 
    FaClock, 
    FaSyncAlt, 
    FaPaperPlane, 
    FaSpinner,
    FaCheckCircle,
    FaExclamationTriangle,
    FaFilePdf,
    FaBolt,
    FaEdit,
    FaTrashAlt,
    FaPlus,
    FaCalendarAlt
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import './ModalEnvioSendGrid.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

const REMITENTE_VERIFICADO = 'domushabitat2025@hotmail.com';
const CORREO_PRUEBA_RAPIDA = 'andywongajwf1990@gmail.com';

const PLANTILLAS_BASE = [
    {
        id: 'presupuesto',
        nombre: 'Presupuesto Formal',
        asunto: 'Cotización & Presupuesto Formal Folio {folio} - {emisor}',
        cuerpo: 'Estimado(a) {cliente}:\n\nAdjuntamos la propuesta técnica y cotización formal correspondiente al folio {folio} por un total de ${total} MXN.\n\nEste presupuesto incluye mano de obra, materiales y condiciones de garantía detalladas en el documento PDF adjunto. Quedamos a sus órdenes para cualquier ajuste o aprobación del proyecto.\n\nAtentamente,\n{emisor}'
    },
    {
        id: 'remision',
        nombre: 'Aviso de Remisión',
        asunto: 'Aviso de Remisión de Cobro Folio {folio} - {emisor}',
        cuerpo: 'Estimado(a) {cliente}:\n\nLe enviamos el aviso de remisión y cobro de servicios con folio {folio} por un monto de ${total} MXN.\n\nEn el documento PDF adjunto encontrará la relación de conceptos ejecutados y las cuentas bancarias para realizar la transferencia o depósito correspondiente.\n\nAtentamente,\n{emisor}'
    },
    {
        id: 'pago',
        nombre: 'Nota de Pago',
        asunto: 'Comprobante de Nota de Pago Folio {folio} - {emisor}',
        cuerpo: 'Estimado(a) {cliente}:\n\nConfirmamos la recepción de su pago con la Nota de Pago folio {folio} por la cantidad de ${total} MXN.\n\nAgradecemos cordialmente su preferencia y cumplimiento. El documento formal en PDF queda adjunto para su archivo contable.\n\nAtentamente,\n{emisor}'
    }
];

const ModalEnvioSendGrid = ({
    isOpen,
    onClose,
    documento,
    totalFinal = 0,
    onConfirmarEnvio,
    enviando = false
}) => {
    const [destinatario, setDestinatario] = useState('');
    const [cc, setCc] = useState('');
    const [asunto, setAsunto] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [adjuntarPdf, setAdjuntarPdf] = useState(true);
    const [modoEnvio, setModoEnvio] = useState('inmediato'); // 'inmediato', 'programado', 'recurrente'
    
    // Fechas para programado
    const [fechaProgramada, setFechaProgramada] = useState('');
    const [horaProgramada, setHoraProgramada] = useState('09:00');

    // Opciones para recurrente con Calendario (Punto 1 y 3)
    const [frecuencia, setFrecuencia] = useState('mensual');
    const [fechaCorte, setFechaCorte] = useState(() => new Date().toISOString().split('T')[0]);

    // Resumen en lenguaje natural del día de corte y frecuencia seleccionada
    const textoResumenCorte = useMemo(() => {
        if (!fechaCorte) return null;
        try {
            const [y, m, d] = fechaCorte.split('-').map(Number);
            const dt = new Date(y, m - 1, d, 12, 0, 0);
            const diaSemana = dt.toLocaleDateString('es-MX', { weekday: 'long' });
            const diaNum = dt.getDate();
            const mesNom = dt.toLocaleDateString('es-MX', { month: 'long' });
            const capSemana = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
            const capMes = mesNom.charAt(0).toUpperCase() + mesNom.slice(1);

            const freqLabels = {
                semanal: `Cada semana los días ${capSemana}`,
                quincenal: `Cada 15 días (a partir del ${capSemana} ${diaNum})`,
                mensual: `Día ${diaNum} de cada mes`,
                bimestral: `Día ${diaNum} cada 2 meses`,
                trimestral: `Día ${diaNum} cada 3 meses`,
                semestral: `Día ${diaNum} cada 6 meses`,
                anual: `Cada año el ${diaNum} de ${capMes}`
            };

            const patron = freqLabels[frecuencia] || `Cada ${frecuencia}`;
            return {
                diaSemana: capSemana,
                fechaFormateada: `${capSemana} ${diaNum} de ${capMes} ${y}`,
                patron
            };
        } catch {
            return null;
        }
    }, [fechaCorte, frecuencia]);

    // Gestión interactiva de plantillas de correo
    const [plantillas, setPlantillas] = useState(() => {
        try {
            const guardadas = localStorage.getItem('facturacion_plantillas_correo');
            if (guardadas) {
                const parsed = JSON.parse(guardadas);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch (e) {
            console.warn('Error al cargar plantillas de correo:', e);
        }
        return PLANTILLAS_BASE;
    });
    const [plantillaSeleccionadaId, setPlantillaSeleccionadaId] = useState('');

    // Detección automática del correo del emisor desde la Sección 1 (Punto 5)
    const correoEmisorDetectado = useMemo(() => {
        const texto = `${documento?.emisor?.contactoInfo || ''} ${documento?.emisor?.email || ''}`;
        const match = texto.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
        return match ? match[0].trim() : null;
    }, [documento?.emisor?.contactoInfo, documento?.emisor?.email]);

    // Sincronizar campos cada vez que se abra el modal o cambie el documento
    useEffect(() => {
        if (!isOpen) return;

        const emailInicial = documento?.cliente?.email?.trim() || CORREO_PRUEBA_RAPIDA;
        setDestinatario(emailInicial);
        setCc('');

        const tipoNombre = documento?.tipoDocumento === 'presupuesto' ? 'Presupuesto Formal' :
                           documento?.tipoDocumento === 'pago' ? 'Nota de Pago' : 'Remisión de Cobro';

        const folioStr = documento?.folio || 'S/F';
        const emisorNom = documento?.emisor?.nombre || 'Domus Habita';
        const clienteNom = documento?.cliente?.nombre || 'Estimado(a) Cliente';
        const totalStr = (Number(totalFinal) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 });

        setAsunto(`${tipoNombre} Folio ${folioStr} - ${emisorNom}`);
        setMensaje(
            `Estimado(a) ${clienteNom}:\n\nPor medio de la presente, le compartimos el ${tipoNombre} con folio oficial ${folioStr} por un importe total de $${totalStr} MXN, emitido por ${emisorNom}.\n\nAdjunto a este correo encontrará el documento oficial en formato PDF con las especificaciones técnicas y partidas detalladas.\n\nQuedamos atentos a cualquier duda o confirmación.\n\nAtentamente,\n${emisorNom}`
        );
        setPlantillaSeleccionadaId('');

        // Sincronizar programación previa si existe en el documento
        if (documento?.programacionEnvio) {
            const prog = documento.programacionEnvio;
            setModoEnvio(prog.modo || (documento.estado === 'programado' ? 'programado' : documento.estado === 'recurrente' ? 'recurrente' : 'inmediato'));
            if (prog.fechaProgramada) setFechaProgramada(prog.fechaProgramada);
            if (prog.horaProgramada) setHoraProgramada(prog.horaProgramada);
            if (prog.frecuencia) setFrecuencia(prog.frecuencia);
            if (prog.fechaCorte) setFechaCorte(prog.fechaCorte);
        } else if (documento?.estado === 'programado') {
            setModoEnvio('programado');
        } else if (documento?.estado === 'recurrente') {
            setModoEnvio('recurrente');
        }
    }, [isOpen, documento, totalFinal]);

    // Validadores de correo
    const emailDestinatarioValido = destinatario.trim().length > 0 && EMAIL_REGEX.test(destinatario.trim());
    
    const validarCcList = () => {
        if (!cc.trim()) return true;
        const emails = cc.split(',').map(e => e.trim()).filter(e => e.length > 0);
        return emails.every(e => EMAIL_REGEX.test(e));
    };

    const ccValido = validarCcList();
    const formularioValido = emailDestinatarioValido && ccValido;

    // Aplicar plantilla seleccionada
    const aplicarPlantilla = (idPlantilla) => {
        setPlantillaSeleccionadaId(idPlantilla);
        if (!idPlantilla) return;
        const pl = plantillas.find(p => p.id === idPlantilla);
        if (!pl) return;

        const clienteNom = documento?.cliente?.nombre || 'Estimado(a) Cliente';
        const folioStr = documento?.folio || 'S/F';
        const totalStr = (Number(totalFinal) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 });
        const emisorNom = documento?.emisor?.nombre || 'Domus Habita';

        let asun = pl.asunto || '';
        asun = asun.replace(/{folio}/g, folioStr).replace(/{emisor}/g, emisorNom).replace(/{cliente}/g, clienteNom).replace(/{total}/g, totalStr);

        let cuer = pl.cuerpo || '';
        cuer = cuer.replace(/{folio}/g, folioStr).replace(/{emisor}/g, emisorNom).replace(/{cliente}/g, clienteNom).replace(/{total}/g, totalStr);

        if (asun) setAsunto(asun);
        if (cuer) setMensaje(cuer);
    };

    // Guardar redacción actual como nueva plantilla
    const handleGuardarNuevaPlantilla = async () => {
        if (!mensaje.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Mensaje vacío',
                text: 'Redacta un mensaje antes de guardarlo como plantilla.',
                confirmButtonColor: '#2b5329'
            });
            return;
        }

        const { value: nombrePlantilla } = await Swal.fire({
            title: 'Guardar Nueva Plantilla',
            input: 'text',
            inputLabel: 'Nombre descriptivo de la plantilla:',
            placeholder: 'Ej. Seguimiento de Cotización, Aviso Urgente...',
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2b5329',
            inputValidator: (value) => {
                if (!value || !value.trim()) {
                    return 'Ingresa un nombre para la plantilla';
                }
            }
        });

        if (nombrePlantilla) {
            const nueva = {
                id: 'custom_' + Date.now(),
                nombre: nombrePlantilla.trim(),
                asunto: asunto.trim(),
                cuerpo: mensaje.trim(),
                esPersonalizada: true
            };
            const actualizadas = [...plantillas, nueva];
            setPlantillas(actualizadas);
            setPlantillaSeleccionadaId(nueva.id);
            localStorage.setItem('facturacion_plantillas_correo', JSON.stringify(actualizadas));
            Swal.fire({
                icon: 'success',
                title: 'Plantilla Guardada',
                text: `La plantilla "${nombrePlantilla}" fue guardada correctamente.`,
                timer: 1800,
                showConfirmButton: false
            });
        }
    };

    // Actualizar plantilla seleccionada con el texto actual
    const handleActualizarPlantilla = async () => {
        const plActual = plantillas.find(p => p.id === plantillaSeleccionadaId);
        if (!plActual) {
            Swal.fire({
                icon: 'info',
                title: 'Selecciona una plantilla',
                text: 'Elige una plantilla en el menú desplegable para actualizarla.',
                confirmButtonColor: '#2b5329'
            });
            return;
        }

        const { value: formValues } = await Swal.fire({
            title: `Actualizar Plantilla`,
            html:
                `<div style="text-align: left; font-size: 13px;">` +
                `<label style="display:block; margin-bottom: 4px; font-weight: 600;">Nombre de la Plantilla:</label>` +
                `<input id="swal-pl-nombre" class="swal2-input" style="width: 90%; margin: 0 0 12px 0;" value="${plActual.nombre}">` +
                `<label style="display:block; margin-bottom: 4px; font-weight: 600;">Asunto sugerido:</label>` +
                `<input id="swal-pl-asunto" class="swal2-input" style="width: 90%; margin: 0 0 12px 0;" value="${plActual.asunto || asunto.trim()}">` +
                `<p style="font-size: 11px; color: #64748b; margin-top: 6px;">* Se guardará el texto redactado actualmente en el cuerpo del mensaje.</p>` +
                `</div>`,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Guardar Cambios',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#2b5329',
            preConfirm: () => {
                const nom = document.getElementById('swal-pl-nombre')?.value;
                const asu = document.getElementById('swal-pl-asunto')?.value;
                if (!nom || !nom.trim()) {
                    Swal.showValidationMessage('El nombre no puede estar vacío');
                    return false;
                }
                return { nombre: nom.trim(), asunto: asu ? asu.trim() : '' };
            }
        });

        if (formValues) {
            const actualizadas = plantillas.map(p => {
                if (p.id === plActual.id) {
                    return {
                        ...p,
                        nombre: formValues.nombre,
                        asunto: formValues.asunto,
                        cuerpo: mensaje.trim()
                    };
                }
                return p;
            });
            setPlantillas(actualizadas);
            localStorage.setItem('facturacion_plantillas_correo', JSON.stringify(actualizadas));
            Swal.fire({
                icon: 'success',
                title: 'Plantilla Actualizada',
                text: `La plantilla "${formValues.nombre}" fue actualizada con éxito.`,
                timer: 1800,
                showConfirmButton: false
            });
        }
    };

    // Eliminar plantilla seleccionada
    const handleEliminarPlantilla = async () => {
        const plActual = plantillas.find(p => p.id === plantillaSeleccionadaId);
        if (!plActual) return;

        const result = await Swal.fire({
            title: `¿Eliminar plantilla?`,
            text: `¿Deseas eliminar la plantilla "${plActual.nombre}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            const actualizadas = plantillas.filter(p => p.id !== plActual.id);
            setPlantillas(actualizadas);
            setPlantillaSeleccionadaId('');
            localStorage.setItem('facturacion_plantillas_correo', JSON.stringify(actualizadas));
            Swal.fire({
                icon: 'success',
                title: 'Plantilla Eliminada',
                timer: 1500,
                showConfirmButton: false
            });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!emailDestinatarioValido) {
            alert('Por favor especifica un correo electrónico destinatario válido.');
            return;
        }
        if (!ccValido) {
            alert('Una o más direcciones en el campo Con Copia (CC) no tienen un formato válido.');
            return;
        }

        const datosEnvio = {
            destinatario: destinatario.trim(),
            cc: cc.trim(),
            remitente: REMITENTE_VERIFICADO,
            remitentePersonalizado: correoEmisorDetectado,
            asunto: asunto.trim(),
            mensaje: mensaje.trim(),
            adjuntarPdf,
            modoEnvio,
            fechaProgramada: modoEnvio === 'programado' ? fechaProgramada : null,
            horaProgramada: (modoEnvio === 'programado' || modoEnvio === 'recurrente') ? horaProgramada : null,
            frecuencia: modoEnvio === 'recurrente' ? frecuencia : null,
            fechaCorte: modoEnvio === 'recurrente' ? fechaCorte : null,
            diaCorte: modoEnvio === 'recurrente' ? (fechaCorte ? Number(fechaCorte.split('-')[2]) : 1) : null,
            proximoEnvio: modoEnvio === 'recurrente' ? fechaCorte : (modoEnvio === 'programado' ? fechaProgramada : null),
            total: totalFinal,
            folio: documento?.folio || '',
            clienteNombre: documento?.cliente?.nombre || '',
            fechaCreacion: new Date().toISOString()
        };

        onConfirmarEnvio(datosEnvio);
    };

    return (
        <div className="modal-sendgrid-overlay" onClick={onClose}>
            <div className="modal-sendgrid-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-sendgrid-header">
                    <h3 className="modal-sendgrid-title">
                        <FaEnvelope style={{ color: '#0284c7' }} /> Emisión de Correo Electrónico
                    </h3>
                    <button type="button" className="modal-sendgrid-close" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Body */}
                    <div className="modal-sendgrid-body">
                        
                        {/* Resumen del documento */}
                        <div className="doc-summary-badge">
                            <div className="doc-summary-item">
                                <span className="doc-summary-label">Documento</span>
                                <span className="doc-summary-val">{documento?.folio || 'Borrador'}</span>
                            </div>
                            <div className="doc-summary-item">
                                <span className="doc-summary-label">Emisor</span>
                                <span className="doc-summary-val" style={{ color: correoEmisorDetectado ? '#0284c7' : '#d97706', fontWeight: '600' }}>
                                    {documento?.emisor?.nombre || 'Domus Habita'}
                                    {correoEmisorDetectado ? ` (${correoEmisorDetectado})` : ' (Sin Correo)'}
                                </span>
                            </div>
                            <div className="doc-summary-item">
                                <span className="doc-summary-label">Cliente</span>
                                <span className="doc-summary-val">{documento?.cliente?.nombre || 'Sin Cliente'}</span>
                            </div>
                            <div className="doc-summary-item">
                                <span className="doc-summary-label">Total a Notificar</span>
                                <span className="doc-summary-val" style={{ color: '#2b5329' }}>
                                    ${(Number(totalFinal) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                                </span>
                            </div>
                        </div>

                        {/* Correo Destinatario con Validadores y Atajos */}
                        <div className="sendgrid-input-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                <label style={{ margin: 0 }}>Correo Electrónico Destinatario *</label>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setDestinatario(CORREO_PRUEBA_RAPIDA)}
                                        style={{ background: '#f8fafc', color: '#0284c7', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px' }}
                                        title="Cargar correo de prueba de Andy Wong"
                                    >
                                        <FaBolt /> {CORREO_PRUEBA_RAPIDA}
                                    </button>
                                    {documento?.cliente?.email && (
                                        <button
                                            type="button"
                                            onClick={() => setDestinatario(documento.cliente.email)}
                                            style={{ background: '#f8fafc', color: '#16a34a', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}
                                        >
                                            Email Cliente
                                        </button>
                                    )}
                                </div>
                            </div>
                            <input 
                                type="email"
                                required
                                className="sendgrid-input-control"
                                placeholder="destinatario@ejemplo.com"
                                value={destinatario}
                                onChange={(e) => setDestinatario(e.target.value)}
                                style={{
                                    borderColor: destinatario && !emailDestinatarioValido ? '#ef4444' : undefined,
                                    background: destinatario && !emailDestinatarioValido ? '#fef2f2' : undefined
                                }}
                            />
                            {destinatario && !emailDestinatarioValido && (
                                <span style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <FaExclamationTriangle /> Formato de correo inválido (ejemplo: usuario@dominio.com)
                                </span>
                            )}
                            {!correoEmisorDetectado && (
                                <div style={{ fontSize: '11px', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '4px', padding: '5px 8px', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <FaExclamationTriangle style={{ color: '#d97706', flexShrink: 0 }} />
                                    <span>⚠️ Hace falta determinar la dirección de correo del emisor en la Sección 1 ("Teléfono y Correo").</span>
                                </div>
                            )}
                        </div>

                        {/* Con Copia (CC) */}
                        <div className="sendgrid-input-group">
                            <label>Con Copia (CC opcional, separados por coma)</label>
                            <input 
                                type="text"
                                className="sendgrid-input-control"
                                placeholder="direccion@ejemplo.com, contabilidad@ejemplo.com"
                                value={cc}
                                onChange={(e) => setCc(e.target.value)}
                                style={{
                                    borderColor: cc && !ccValido ? '#ef4444' : undefined,
                                    background: cc && !ccValido ? '#fef2f2' : undefined
                                }}
                            />
                            {cc && !ccValido && (
                                <span style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <FaExclamationTriangle /> Uno o más correos en copia tienen formato inválido
                                </span>
                            )}
                        </div>

                        {/* Asunto */}
                        <div className="sendgrid-input-group">
                            <label>Asunto del Correo</label>
                            <input 
                                type="text"
                                required
                                className="sendgrid-input-control"
                                value={asunto}
                                onChange={(e) => setAsunto(e.target.value)}
                            />
                        </div>

                        {/* Plantillas Rápidas y Mensaje con Botones de Guardar / Editar */}
                        <div className="sendgrid-input-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                                <label style={{ margin: 0, fontWeight: 700 }}>Mensaje / Cuerpo de Notificación</label>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <select
                                        className="sendgrid-input-control"
                                        style={{ padding: '3px 8px', fontSize: '11px', height: '28px', minWidth: '150px' }}
                                        value={plantillaSeleccionadaId}
                                        onChange={(e) => aplicarPlantilla(e.target.value)}
                                    >
                                        <option value="">-- Cargar Plantilla --</option>
                                        {plantillas.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.nombre} {p.esPersonalizada ? '★' : ''}
                                            </option>
                                        ))}
                                    </select>

                                    <button
                                        type="button"
                                        onClick={handleGuardarNuevaPlantilla}
                                        style={{
                                            background: '#f0fdf4',
                                            color: '#16a34a',
                                            border: '1px solid #bbf7d0',
                                            borderRadius: '4px',
                                            padding: '4px 8px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                        title="Guardar redacción actual como nueva plantilla"
                                    >
                                        <FaPlus /> Guardar
                                    </button>

                                    {plantillaSeleccionadaId && (
                                        <>
                                            <button
                                                type="button"
                                                onClick={handleActualizarPlantilla}
                                                style={{
                                                    background: '#eff6ff',
                                                    color: '#0284c7',
                                                    border: '1px solid #bfdbfe',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                                title="Actualizar la plantilla con los cambios actuales"
                                            >
                                                <FaEdit /> Editar
                                            </button>

                                            <button
                                                type="button"
                                                onClick={handleEliminarPlantilla}
                                                style={{
                                                    background: '#fef2f2',
                                                    color: '#dc2626',
                                                    border: '1px solid #fecaca',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    fontSize: '11px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                title="Eliminar plantilla seleccionada"
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            <textarea 
                                rows={4}
                                className="sendgrid-input-control"
                                style={{ resize: 'vertical' }}
                                value={mensaje}
                                onChange={(e) => setMensaje(e.target.value)}
                            />
                        </div>

                        {/* Checkbox de Documento PDF Adjunto (Punto 7) */}
                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: 700, color: '#0f172a', margin: 0, cursor: 'pointer' }}>
                                <input 
                                    type="checkbox"
                                    checked={adjuntarPdf}
                                    onChange={(e) => setAdjuntarPdf(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                <FaFilePdf style={{ color: '#dc2626', fontSize: '14px' }} />
                                <span>Adjuntar Documento Oficial en formato PDF de alta definición</span>
                            </label>
                            <span style={{ fontSize: '11px', color: '#16a34a', fontWeight: 700, background: '#dcfce7', padding: '2px 8px', borderRadius: '10px' }}>
                                Generación Automática
                            </span>
                        </div>

                        {/* Modalidad de Envío */}
                        <div className="sendgrid-input-group">
                            <label>Planificación y Modalidad de Entrega</label>
                            <div className="send-mode-selector">
                                <button 
                                    type="button" 
                                    className={`send-mode-btn ${modoEnvio === 'inmediato' ? 'active' : ''}`}
                                    onClick={() => setModoEnvio('inmediato')}
                                >
                                    <FaPaperPlane />
                                    <span>Inmediato</span>
                                </button>
                                <button 
                                    type="button" 
                                    className={`send-mode-btn ${modoEnvio === 'programado' ? 'active' : ''}`}
                                    onClick={() => setModoEnvio('programado')}
                                >
                                    <FaClock />
                                    <span>Programado</span>
                                </button>
                                <button 
                                    type="button" 
                                    className={`send-mode-btn ${modoEnvio === 'recurrente' ? 'active' : ''}`}
                                    onClick={() => setModoEnvio('recurrente')}
                                >
                                    <FaSyncAlt />
                                    <span>Recurrente</span>
                                </button>
                            </div>
                        </div>

                        {/* Opciones si es Programado */}
                        {modoEnvio === 'programado' && (
                            <div className="schedule-fields-box">
                                <div className="schedule-row">
                                    <div className="schedule-col">
                                        <label>Fecha de Envío (CST)</label>
                                        <input 
                                            type="date"
                                            required
                                            className="sendgrid-input-control"
                                            value={fechaProgramada}
                                            onChange={(e) => setFechaProgramada(e.target.value)}
                                        />
                                    </div>
                                    <div className="schedule-col">
                                        <label>Hora de Envío (CST)</label>
                                        <input 
                                            type="time"
                                            required
                                            className="sendgrid-input-control"
                                            value={horaProgramada}
                                            onChange={(e) => setHoraProgramada(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Opciones si es Recurrente con Calendario (Punto 1 y 4) */}
                        {modoEnvio === 'recurrente' && (
                            <div className="schedule-fields-box">
                                <div className="schedule-row" style={{ gap: '12px', alignItems: 'flex-end' }}>
                                    <div className="schedule-col" style={{ flex: '1 1 160px' }}>
                                        <label>Frecuencia de Envío</label>
                                        <select 
                                            className="sendgrid-input-control"
                                            value={frecuencia}
                                            onChange={(e) => setFrecuencia(e.target.value)}
                                        >
                                            <option value="semanal">Semanal (Cada semana)</option>
                                            <option value="quincenal">Quincenal (Cada 15 días)</option>
                                            <option value="mensual">Mensual (Cada mes)</option>
                                            <option value="bimestral">Bimestral (Cada 2 meses)</option>
                                            <option value="trimestral">Trimestral (Cada 3 meses)</option>
                                            <option value="semestral">Semestral (Cada 6 meses)</option>
                                            <option value="anual">Anual (Cada 12 meses)</option>
                                        </select>
                                    </div>
                                    <div className="schedule-col" style={{ flex: '1 1 160px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <FaCalendarAlt style={{ color: '#0284c7' }} />
                                            Día de Corte / Emisión
                                        </label>
                                        <input 
                                            type="date"
                                            required
                                            className="sendgrid-input-control"
                                            value={fechaCorte}
                                            onChange={(e) => setFechaCorte(e.target.value)}
                                        />
                                    </div>
                                    <div className="schedule-col" style={{ flex: '0 0 110px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <FaClock style={{ color: '#0284c7' }} />
                                            Hora (CST)
                                        </label>
                                        <input 
                                            type="time"
                                            required
                                            className="sendgrid-input-control"
                                            value={horaProgramada}
                                            onChange={(e) => setHoraProgramada(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {textoResumenCorte && (
                                    <div style={{
                                        background: '#f0fdf4',
                                        border: '1px solid #bbf7d0',
                                        borderRadius: '6px',
                                        padding: '8px 12px',
                                        fontSize: '11.5px',
                                        color: '#166534',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        marginTop: '4px'
                                    }}>
                                        <FaSyncAlt style={{ flexShrink: 0, color: '#16a34a' }} />
                                        <span>
                                            Primer corte / emisión: <strong>{textoResumenCorte.fechaFormateada}</strong> a las <strong>{horaProgramada} hrs</strong> • Ciclo: <strong>{textoResumenCorte.patron}</strong>
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="modal-sendgrid-footer">
                        <button 
                            type="button" 
                            className="btn-modal-cancel" 
                            onClick={onClose}
                            disabled={enviando}
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            className="btn-modal-send"
                            disabled={enviando || !formularioValido}
                            style={{
                                opacity: !formularioValido ? 0.6 : 1,
                                cursor: !formularioValido ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {enviando ? (
                                <>
                                    <FaSpinner className="fa-spin" /> Enviando...
                                </>
                            ) : (
                                <>
                                    <FaPaperPlane /> {modoEnvio === 'inmediato' ? 'Enviar Ahora' : 'Programar Envío'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ModalEnvioSendGrid;
